import React from 'react';
import moment from 'moment';
import LocalizedMessage from 'common/localized-message';
import { AvatarTeam, Link, Text } from 'components/shared/uniform';
import { getSportByName } from 'components/shared/uniform/sport-helpers';

const nonBreakingHyphen = String.fromCharCode(8209);

class ScheduleEvent extends React.Component {
  static propTypes = {
    boxScoreUrl: React.PropTypes.string,
    convertTimesToLocal: React.PropTypes.bool,
    followerCount: React.PropTypes.number,
    forcePublicView: React.PropTypes.bool,
    gameId: React.PropTypes.string,
    hasGameVideo: React.PropTypes.bool,
    hasScore: React.PropTypes.bool.isRequired,
    isPrivilegedUser: React.PropTypes.bool,
    isHome: React.PropTypes.bool,
    isTie: React.PropTypes.bool,
    isWin: React.PropTypes.bool,
    isPractice: React.PropTypes.bool,
    isScrimmage: React.PropTypes.bool,
    manageScheduleUrl: React.PropTypes.string,
    opponentLogoUri: React.PropTypes.string,
    opponentName: React.PropTypes.string.isRequired,
    opponentScore: React.PropTypes.number,
    opponentTeamId: React.PropTypes.string,
    ownScore: React.PropTypes.number,
    schoolAbbreviation: React.PropTypes.string,
    primaryColor: React.PropTypes.string,
    secondaryColor: React.PropTypes.string,
    sportName: React.PropTypes.string,
    seasonId: React.PropTypes.string,
    teamProfileUrlBase: React.PropTypes.string.isRequired,
    utcTime: React.PropTypes.string,
  }

  constructor() {
    super();
    this.formatDate = this.formatDate.bind(this);
    this.formatTime = this.formatTime.bind(this);
    this.getLogo = this.getLogo.bind(this);
    this.getOpponent = this.getOpponent.bind(this);
    this.getScore = this.getScore.bind(this);
  }

  formatDate() {
    if (!this.props.utcTime) {
      return null;
    }

    // Grab time offset from end of string
    let parsedDate;
    if (this.props.convertTimesToLocal) {
      parsedDate = moment.utc(this.props.utcTime.slice(0, -6)).local();
    } else {
      parsedDate = moment.utc(this.props.utcTime).utcOffset(this.props.utcTime.slice(-6));
    }

    const day = parsedDate.format('D');
    const dayOfWeek = parsedDate.format('dddd');
    let month = parsedDate.format('MMMM');

    if (month === 'September') {
      month = 'Sept.';
    } else {
      month = month.length > 5 ? month.substring(0, 3) + '.' : month;
    }

    const dmy = `${dayOfWeek}, ${day} ${month}`;

    return dmy;
  }

  formatTime() {
    if (!this.props.utcTime) {
      return null;
    }

    let parsedTime;
    // Grab time offset from end of string
    if (this.props.convertTimesToLocal) {
      parsedTime = moment.utc(this.props.utcTime.slice(0, -6)).local();
    } else {
      parsedTime = moment.utc(this.props.utcTime).utcOffset(this.props.utcTime.slice(-6));
    }

    const time = parsedTime.format('h:mm');
    const amPm = parsedTime.format('a');

    const fullTime = `${time} ${amPm.substring(0, 1)}.${amPm.substring(1, 2)}.`;

    return fullTime;
  }

  getLogo() {
    if (this.props.opponentTeamId) {
      return (
        <Link type="wrapper" href={this.props.teamProfileUrlBase + this.props.opponentTeamId}>
          <AvatarTeam
            size="medium"
            imageUrl={this.props.opponentLogoUri}
            sport={getSportByName(this.props.sportName)}
            primaryColor={this.props.primaryColor}
            secondaryColor={this.props.secondaryColor}
          />
        </Link>
      );
    }

    return (
      <AvatarTeam
        size="medium"
        imageUrl={this.props.opponentLogoUri}
        sport={getSportByName(this.props.sportName)}
        primaryColor={this.props.primaryColor}
        secondaryColor={this.props.secondaryColor}
      />
    );
  }

  getOpponent() {
    if (this.props.opponentTeamId) {
      return (
        <Link type="implied" href={this.props.teamProfileUrlBase + this.props.opponentTeamId}>
          {this.props.opponentName}
        </Link>
      );
    }

    return this.props.opponentName;
  }

  getScore() {
    if (!this.props.hasScore) {
      if (this.props.isPrivilegedUser && !this.props.forcePublicView && !this.props.isPractice &&
        !this.props.isScrimmage) {
        const gameIsInPast = moment(this.props.utcTime).isBefore(moment());

        if (gameIsInPast) {
          const manageUrl = `${this.props.manageScheduleUrl}/${this.props.seasonId}/entry/${this.props.gameId}`;
          return (
            <a className="uni-btn--primary--minimal uni-btn--small" href={manageUrl}>Enter Score</a>
          );
        }
      }

      return null;
    }

    let resultLabel;
    let score;
    if (this.props.isWin) {
      resultLabel = 'Win';
      score = (
        <Text level="small">
          <strong>{this.props.ownScore}</strong> {nonBreakingHyphen} {this.props.opponentScore}
        </Text>
      );
    } else if (this.props.isTie) {
      resultLabel = <LocalizedMessage id="profiles.team.schedule.tie"/>;
      score = (
        <Text level="small">
          {this.props.ownScore} {nonBreakingHyphen} {this.props.opponentScore}
        </Text>
      );
    } else {
      resultLabel = 'Loss';
      score = (
        <Text level="small">
          {this.props.ownScore} {nonBreakingHyphen} <strong>{this.props.opponentScore}</strong>
        </Text>
      );
    }

    if (this.props.boxScoreUrl) {
      return (
        <a className="uni-link--wrapper" href={'/' + this.props.boxScoreUrl}>
          <div className="score">
            <span className={this.props.isWin ? 'win' : 'loss'}>
              <Text level="small"><strong>{resultLabel}</strong><br/>{score}</Text>
            </span>
          </div>
        </a>
      );
    }

    return (
      <div className="score">
        <span className={this.props.isWin ? 'win' : 'loss'}>
          <Text level="small"><strong>{resultLabel}</strong><br/>{score}</Text>
        </span>
      </div>
    );
  }

  render() {
    const date = this.formatDate();
    const time = this.formatTime();
    const score = this.getScore();

    return (
      <li className="schedule-event">
        <div className="team-logo">
          {this.getLogo()}
        </div>
        <div className="col-opponent">
          <Text>
            {this.props.isHome ? 'vs ' : '@ '}
            {this.getOpponent()}
          </Text>
          <Text level="small" color="nonessential">
            {date} at {time}
          </Text>
        </div>
        <div className="col-date">
          <Text>{date} at {time}</Text>
        </div>
        <div className="col-score">{score}</div>
      </li>
    );
  }
}

export default ScheduleEvent;
